import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import projects from '../data/portfolioProjects';
import { localizeProject } from '../data/portfolioProjects.i18n';
import { useT, useLang } from '../i18n/LanguageContext';

const FILTERS = ['ALL', 'MICE', 'EVENTS', 'WEDDINGS'];

const eyebrowStyle = {
  fontFamily: 'Inter, sans-serif',
  fontSize: '9pt',
  fontWeight: 600,
  letterSpacing: '0.32em',
  textTransform: 'uppercase',
  color: '#D6A87A',
};

/* ── Card ── */
const ProjectCard = ({ project: rawProject, mode = 'grid' }) => {
  const t = useT();
  const { lang } = useLang();
  const project = localizeProject(rawProject, lang);
  const isLarge = project.size === 'large';
  const aspect = isLarge ? '4/3' : '4/5';

  // List mode → all cards full width with landscape aspect
  const cardAspect = mode === 'list' ? '21/9' : aspect;

  return (
    <Link
      to={`/portfolio/${project.slug}`}
      data-testid={`portfolio-card-${project.slug}`}
      className="group relative block overflow-hidden"
      style={{
        aspectRatio: cardAspect,
        borderRadius: '1px',
      }}
    >
      <img
        src={project.cover}
        alt={project.title}
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.04]"
      />

      {/* Navy bottom overlay, bottom 50% of card */}
      <div
        className="absolute inset-0 pointer-events-none transition-opacity duration-500"
        style={{
          background:
            'linear-gradient(to bottom, rgba(10,19,47,0) 50%, rgba(10,19,47,0.85) 100%)',
        }}
      />
      {/* Hover deepens overlay 10% */}
      <div
        className="absolute inset-0 pointer-events-none opacity-0 transition-opacity duration-500 group-hover:opacity-100"
        style={{
          background:
            'linear-gradient(to bottom, rgba(10,19,47,0) 50%, rgba(10,19,47,0.95) 100%)',
        }}
      />

      {/* Category chip */}
      <div
        className="absolute top-5 left-5 z-10 inline-flex items-center"
        style={{
          height: '24px',
          padding: '0 12px',
          fontFamily: 'Inter, sans-serif',
          fontSize: '9pt',
          fontWeight: 600,
          letterSpacing: '0.22em',
          textTransform: 'uppercase',
          color: '#D6A87A',
          border: '1px solid rgba(214,168,122,0.6)',
          backgroundColor: 'transparent',
        }}
      >
        {t(`filters.${project.category.toLowerCase()}`)}
      </div>

      {/* Bottom content */}
      <div className="absolute bottom-0 left-0 right-0 p-7 md:p-8 z-10">
        <div
          style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: '8pt',
            fontWeight: 600,
            letterSpacing: '0.32em',
            textTransform: 'uppercase',
            color: 'rgba(214,168,122,0.8)',
          }}
          className="mb-2"
        >
          {project.eyebrowDate}
        </div>

        <h3
          className="transition-transform duration-500 group-hover:scale-[1.04] origin-left"
          style={{
            fontFamily: '"Cormorant Garamond", serif',
            fontSize: 'clamp(20px, 1.6vw, 24pt)',
            lineHeight: 1.15,
            fontWeight: 500,
            color: '#D6A87A',
            letterSpacing: '-0.005em',
          }}
        >
          {project.title}
        </h3>

        {/* Hover hairline */}
        <div
          className="mt-3 h-px transition-[width] duration-500"
          style={{
            width: '0',
            backgroundColor: 'rgba(214,168,122,0.65)',
          }}
          data-card-rule
        />
        <style>{`
          [data-testid="portfolio-card-${project.slug}"]:hover [data-card-rule] {
            width: 56px;
          }
        `}</style>

        <p
          className="mt-3 max-w-[280px]"
          style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: '11pt',
            fontWeight: 400,
            lineHeight: 1.55,
            color: 'rgba(244,239,227,0.82)',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {project.brief}
        </p>

        <div
          className="mt-4"
          style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: '9pt',
            fontWeight: 600,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color: '#D6A87A',
          }}
        >
          {project.numbers.map((n) => `${n.value} ${n.label}`).join(' · ')}
        </div>
      </div>
    </Link>
  );
};

const Portfolio = () => {
  const t = useT();
  const [filter, setFilter] = useState('ALL');
  const [mode, setMode] = useState('grid');

  const visible = useMemo(() => {
    if (filter === 'ALL') return projects;
    return projects.filter((p) => p.category === filter);
  }, [filter]);

  return (
    <div data-testid="portfolio-page">
      <SEO
        title="Portfolio, Selected Gatherings | GlowMeets"
        description="Six rooms we built, congresses, evenings and ceremonies across Türkiye. Each one its own chapter, each one its own photograph."
        keywords="GlowMeets portfolio, MICE case studies Turkey, destination wedding portfolio, event design portfolio Istanbul"
        url="/portfolio"
      />

      {/* ───────────────────────── 1. HERO ───────────────────────── */}
      <section
        className="bg-navy w-full flex items-center justify-center"
        style={{ minHeight: '100vh', paddingTop: '120px', paddingBottom: '90px' }}
        data-testid="portfolio-hero"
      >
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div style={eyebrowStyle} className="mb-7">
            {t('portfolio.hero.eyebrow')}
          </div>
          <h1
            className="text-bone mx-auto"
            style={{
              fontFamily: '"Cormorant Garamond", serif',
              fontSize: 'clamp(40px, 5vw, 64pt)',
              lineHeight: 1.05,
              fontWeight: 500,
              letterSpacing: '-0.005em',
              maxWidth: '20ch',
            }}
          >
            <span className="block">{t('portfolio.hero.line1')}</span>
            <span
              className="block italic"
              style={{ color: '#D6A87A', fontWeight: 400 }}
            >
              {t('portfolio.hero.line2')}
            </span>
          </h1>
          <p
            className="mt-7 mx-auto"
            style={{
              fontFamily: '"Cormorant Garamond", serif',
              fontStyle: 'italic',
              fontWeight: 400,
              fontSize: '18pt',
              color: '#D6A87A',
              lineHeight: 1.5,
              maxWidth: '56ch',
            }}
          >
            {t('portfolio.hero.sub')}
          </p>
          <div className="mt-12 flex justify-center">
            <span
              style={{
                display: 'block',
                width: '1px',
                height: '60px',
                backgroundColor: 'rgba(214,168,122,0.6)',
              }}
            />
          </div>
        </div>
      </section>

      {/* ───────────────────────── 2. STICKY FILTER STRIP ───────────────────────── */}
      <div
        className="sticky top-0 z-40 w-full"
        style={{
          backgroundColor: '#0A132F',
          height: '60px',
          borderBottom: '1px solid rgba(214,168,122,0.35)',
        }}
        data-testid="portfolio-filter-strip"
      >
        <div className="h-full max-w-container mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between gap-6">
          {/* Filters */}
          <nav className="flex items-center overflow-x-auto whitespace-nowrap" data-testid="portfolio-filters">
            {FILTERS.map((f, idx) => {
              const active = filter === f;
              return (
                <React.Fragment key={f}>
                  <button
                    type="button"
                    onClick={() => setFilter(f)}
                    data-testid={`pf-filter-${f.toLowerCase()}`}
                    className="transition-all duration-200"
                    style={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '11pt',
                      fontWeight: 500,
                      letterSpacing: active ? '0.24em' : '0.22em',
                      textTransform: 'uppercase',
                      color: '#D6A87A',
                      borderBottom: active
                        ? '1px solid #D6A87A'
                        : '1px solid transparent',
                      paddingBottom: '4px',
                      background: 'transparent',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.letterSpacing = '0.24em';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.letterSpacing = active ? '0.24em' : '0.22em';
                    }}
                  >
                    {t(`filters.${f.toLowerCase()}`)}
                  </button>
                  {idx < FILTERS.length - 1 && (
                    <span
                      aria-hidden="true"
                      className="mx-3 select-none"
                      style={{ color: 'rgba(214,168,122,0.45)' }}
                    >
                      ·
                    </span>
                  )}
                </React.Fragment>
              );
            })}
          </nav>

          {/* Grid/List toggle */}
          <div className="flex items-center" data-testid="portfolio-mode-toggle">
            <button
              type="button"
              onClick={() => setMode('grid')}
              data-testid="pf-mode-grid"
              aria-label="Grid view"
              className="px-2"
              style={{
                background: 'transparent',
                color: mode === 'grid' ? '#D6A87A' : 'rgba(214,168,122,0.45)',
                transition: 'color 0.25s ease',
              }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <rect x="1" y="1" width="6" height="6" stroke="currentColor" strokeWidth="1.2" />
                <rect x="9" y="1" width="6" height="6" stroke="currentColor" strokeWidth="1.2" />
                <rect x="1" y="9" width="6" height="6" stroke="currentColor" strokeWidth="1.2" />
                <rect x="9" y="9" width="6" height="6" stroke="currentColor" strokeWidth="1.2" />
              </svg>
            </button>
            <span
              aria-hidden="true"
              className="mx-2"
              style={{ color: 'rgba(214,168,122,0.45)' }}
            >
              /
            </span>
            <button
              type="button"
              onClick={() => setMode('list')}
              data-testid="pf-mode-list"
              aria-label="List view"
              className="px-2"
              style={{
                background: 'transparent',
                color: mode === 'list' ? '#D6A87A' : 'rgba(214,168,122,0.45)',
                transition: 'color 0.25s ease',
              }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <line x1="1" y1="3" x2="15" y2="3" stroke="currentColor" strokeWidth="1.2" />
                <line x1="1" y1="8" x2="15" y2="8" stroke="currentColor" strokeWidth="1.2" />
                <line x1="1" y1="13" x2="15" y2="13" stroke="currentColor" strokeWidth="1.2" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* ───────────────────────── 3. GRID ───────────────────────── */}
      <section
        className="bg-white"
        style={{ paddingTop: '90px', paddingBottom: '90px', paddingLeft: '60px', paddingRight: '60px' }}
        data-testid="portfolio-grid-section"
      >
        <div
          key={`${filter}-${mode}`}
          className="max-w-[1400px] mx-auto fade-in"
          style={{ animationDuration: '280ms' }}
        >
          {mode === 'list' ? (
            <div className="flex flex-col gap-8">
              {visible.map((p) => (
                <ProjectCard key={p.slug} project={p} mode="list" />
              ))}
            </div>
          ) : (
            <div
              className="grid grid-cols-1 md:grid-cols-3"
              style={{ gap: '32px' }}
            >
              {visible.map((p) => (
                <div
                  key={p.slug}
                  className={p.size === 'large' ? 'md:col-span-2' : 'md:col-span-1'}
                >
                  <ProjectCard project={p} />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Portfolio;
